import React from 'react';
import { Row } from 'talent_matching/types';
import { withLocalePrefix } from 'shared/utils/translation';
import { Body } from '@grnhse/seedling/lib/birch/typography';
import { Row as BirchRow } from 'shared/components/birch/row';
import { IconButton } from '@grnhse/seedling/lib/birch/components/buttons';
import BackIcon from '@grnhse/seedling/lib/birch/icons/back';
import ForwardIcon from '@grnhse/seedling/lib/birch/icons/forward';
import styled from 'styled-components';
import { FullPalette } from '@grnhse/seedling/lib/birch/colors';
import Tokens from '@grnhse/seedling/lib/birch/tokens';

const t = withLocalePrefix('talent_matching');

type RowStepperProps = {
  currentIndex: number | null | undefined;
  rowCount: number | undefined;
  previousCandidate: Row | null | undefined;
  nextCandidate: Row | null | undefined;
  selectCandidate: ((row: Row) => void) | undefined;
};

type StepTarget = 'previous' | 'next';

// ARIA Toolbar pattern (APG): the control is a single tab stop; Left/Right move
// focus between the < and > buttons (roving tabindex) without navigating, and
// Enter/Space (native button activation) trigger navigation.
const StepperContainer = styled.div`
  border-top: 1px solid ${FullPalette.GREY_E500};
  display: flex;
  padding: ${Tokens.spacing['2']};
  flex-direction: column;
  align-items: center;
`;

const Row = styled(BirchRow)`
  button {
    &:active,
    &:focus-within {
      outline: none;
      background-color: unset;
    }
    /* Visible keyboard focus indicator (WCAG 2.4.7). Scoped to :focus-visible so
       mouse clicks don't show a ring; defined after :focus-within so it wins when
       the button is focused via keyboard. */
    &:focus-visible {
      outline: 2px solid ${FullPalette.EVERGREEN_700};
      outline-offset: 2px;
      background-color: ${FullPalette.IRIS_E50};
    }
    &:hover:not(:disabled) {
      background-color: ${FullPalette.IRIS_E50};
    }
  }
`;

const RowStepper = ({
  currentIndex,
  rowCount,
  previousCandidate,
  nextCandidate,
  selectCandidate,
}: RowStepperProps) => {
  const previousRef = React.useRef<HTMLButtonElement>(null);
  const nextRef = React.useRef<HTMLButtonElement>(null);
  // When navigation is triggered from the toolbar, keep focus on the control
  // after the candidate updates (rather than leaking to the panel/body).
  const retainFocusRef = React.useRef(false);

  const hasPrevious = Boolean(previousCandidate);
  const hasNext = Boolean(nextCandidate);

  const [rovingTarget, setRovingTarget] =
    React.useState<StepTarget>('previous');

  // The roving tab stop must land on an enabled button. If the preferred target
  // is disabled (first/last candidate), fall back to the enabled sibling.
  const tabStop: StepTarget | null =
    rovingTarget === 'previous' && hasPrevious
      ? 'previous'
      : rovingTarget === 'next' && hasNext
      ? 'next'
      : hasPrevious
      ? 'previous'
      : hasNext
      ? 'next'
      : null;

  const focusTarget = (target: StepTarget | null) => {
    if (target === 'previous') {
      previousRef.current?.focus();
    } else if (target === 'next') {
      nextRef.current?.focus();
    }
  };

  // Focus retention + disabled-edge shift: after the candidate set updates, return
  // focus to the active tab stop. At the first/last candidate the pressed button
  // becomes disabled, so `tabStop` has already resolved to the enabled sibling.
  React.useEffect(() => {
    if (!retainFocusRef.current) {
      return;
    }
    retainFocusRef.current = false;
    focusTarget(tabStop);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  const navigate = (target: StepTarget) => {
    const candidate = target === 'previous' ? previousCandidate : nextCandidate;
    if (!candidate || !selectCandidate) {
      return;
    }
    retainFocusRef.current = true;
    setRovingTarget(target);
    selectCandidate(candidate);
  };

  // Arrow keys only move focus between the buttons — they do not navigate.
  const moveFocus = (target: StepTarget) => {
    const enabled = target === 'previous' ? hasPrevious : hasNext;
    if (!enabled) {
      return;
    }
    setRovingTarget(target);
    focusTarget(target);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowLeft':
        event.preventDefault();
        moveFocus('previous');
        break;
      case 'ArrowRight':
        event.preventDefault();
        moveFocus('next');
        break;
      case 'Home':
        event.preventDefault();
        moveFocus(hasPrevious ? 'previous' : 'next');
        break;
      case 'End':
        event.preventDefault();
        moveFocus(hasNext ? 'next' : 'previous');
        break;
      default:
        // Up/Down and everything else are left for the rest of the page.
        break;
    }
  };

  return (
    <StepperContainer
      role="toolbar"
      aria-label={t('candidate_navigation')}
      aria-orientation="horizontal"
      onKeyDown={handleKeyDown}
    >
      <Row spacing={1} alignItems="center">
        <IconButton
          ref={previousRef}
          icon={BackIcon}
          onClick={() => navigate('previous')}
          aria-label={t('previous_candidate')}
          label={t('previous_candidate')}
          disabled={!hasPrevious}
          focusable={tabStop === 'previous'}
        />
        <Body metadata>
          <span aria-live="polite" aria-atomic="true">
            {t('candidate_row_number_of_row_count', {
              row_number: (currentIndex || 0) + 1,
              row_count: rowCount,
            })}
          </span>
        </Body>
        <IconButton
          ref={nextRef}
          icon={ForwardIcon}
          onClick={() => navigate('next')}
          aria-label={t('next_candidate')}
          label={t('next_candidate')}
          disabled={!hasNext}
          focusable={tabStop === 'next'}
        />
      </Row>
    </StepperContainer>
  );
};

export default RowStepper;
